import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api, formatApiError, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Camera, CameraOff, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export default function DoorScanner() {
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const qr = useRef(null);
  const lock = useRef(false);

  const check = async (raw) => {
    if (!raw || lock.current) return;
    lock.current = true; setBusy(true);
    try {
      const { data } = await api.post("/organizer/checkin", { code: raw });
      setResult({ kind: data.already ? "already" : "ok", ...data });
      setHistory((h) => [{ code: data.booking.booking_code, name: data.booking.user_name, ok: !data.already, at: new Date().toISOString() }, ...h].slice(0, 20));
      if (navigator.vibrate) navigator.vibrate(data.already ? [80, 60, 80] : 120);
    } catch (e) {
      setResult({ kind: "bad", message: formatApiError(e), code: raw });
    } finally {
      setBusy(false); setCode("");
      setTimeout(() => { lock.current = false; }, 1800);
    }
  };

  const start = async () => {
    try {
      qr.current = new Html5Qrcode("qr-reader");
      await qr.current.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 240, height: 240 } }, (text) => check(text), () => {});
      setScanning(true);
    } catch (e) {
      setResult({ kind: "bad", message: "Camera unavailable — type the code below instead." });
    }
  };
  const stop = async () => { try { await qr.current?.stop(); qr.current?.clear(); } catch {} setScanning(false); };
  useEffect(() => () => { qr.current?.stop().catch(() => {}); }, []);

  const b = result?.booking;
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <Link to="/organizer" className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400"><ArrowLeft className="h-4 w-4" /> Back to console</Link>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Door scanner</div>
        <h1 className="font-display text-3xl font-extrabold text-zinc-50">Scan tickets at the entrance</h1>
      </div>

      <div className="overflow-hidden rounded-3xl border border-zinc-800 bg-[#3A1020]">
        <div id="qr-reader" data-testid="qr-reader" className={`${scanning ? "block" : "hidden"} [&_video]:w-full`} />
        {!scanning && <div className="flex aspect-video flex-col items-center justify-center text-zinc-500"><Camera className="mb-2 h-8 w-8 text-amber-500" /><span className="text-sm">Camera is off</span></div>}
        <div className="flex gap-2 border-t border-zinc-800 p-3">
          {scanning
            ? <Button data-testid="scanner-stop" onClick={stop} variant="outline" className="flex-1 border-zinc-700 bg-transparent text-zinc-100"><CameraOff className="mr-2 h-4 w-4" />Stop camera</Button>
            : <Button data-testid="scanner-start" onClick={start} className="flex-1 bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400"><Camera className="mr-2 h-4 w-4" />Start camera</Button>}
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); check(code); }} className="mt-4 flex gap-2">
        <Input data-testid="scanner-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Or type code: AH-XXXXXXXX" className="h-12 border-zinc-800 bg-[#3A1020] font-mono uppercase text-zinc-100" />
        <Button data-testid="scanner-code-submit" type="submit" disabled={busy || !code} className="h-12 bg-punch-500 font-bold text-white hover:bg-punch-600">Check in</Button>
      </form>

      {result && (
        <div data-testid={`scan-result-${result.kind}`} className={`mt-4 rounded-2xl border p-5 ${result.kind === "ok" ? "border-amber-500/50 bg-amber-500/10" : result.kind === "already" ? "border-yellow-500/50 bg-yellow-500/10" : "border-red-500/50 bg-red-500/10"}`}>
          <div className="flex items-center gap-3">
            {result.kind === "ok" ? <CheckCircle2 className="h-8 w-8 text-amber-400" /> : result.kind === "already" ? <AlertTriangle className="h-8 w-8 text-yellow-400" /> : <XCircle className="h-8 w-8 text-red-400" />}
            <div>
              <div className="font-display text-xl font-extrabold text-zinc-50">{result.kind === "ok" ? "Welcome in!" : result.kind === "already" ? "Already checked in" : "Invalid ticket"}</div>
              <div className="text-sm text-zinc-300">{b ? `${b.user_name} · ${b.tier_name} × ${b.quantity}${b.seats?.length ? ` · seats ${b.seats.join(", ")}` : ""} · ${b.event_title}` : result.message}</div>
              {result.kind === "already" && <div className="text-xs text-zinc-400">First scanned {formatDate(result.checked_in_at)}</div>}
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6 rounded-2xl border border-zinc-800 bg-[#3A1020] p-4">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Tonight's scans</div>
          {history.map((h, i) => <div key={i} className="flex justify-between border-t border-zinc-800/60 py-1.5 text-sm first:border-0"><span className="text-zinc-200">{h.name}</span><span className={`font-mono text-xs ${h.ok ? "text-amber-400" : "text-yellow-400"}`}>{h.code}</span></div>)}
        </div>
      )}
    </div>
  );
}
